/**
 * ADDITIVE CLINICAL AI AUDIO UTILITY
 * Native Web Audio API synthesis for high-tech, eye-safe clinical acoustics.
 * Free from external dependency or file network loads.
 */

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  // Resume context if suspended (browser security autoplay policies)
  if (audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

/**
 * Synthesizes a futuristic, soft AI digital hover pulse sound (cybernetic chime).
 */
export function playAiHoverSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    // Main oscillator for futuristic high-tech tone
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gainNode = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(880, now); // A5 high note
    osc.frequency.exponentialRampToValueAtTime(1400, now + 0.15);

    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(440, now); // A4 lower harmony
    osc2.frequency.exponentialRampToValueAtTime(880, now + 0.12);

    gainNode.gain.setValueAtTime(0.06, now); // safe, subtle volume
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

    osc.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.start(now);
    osc2.start(now);
    osc.stop(now + 0.2);
    osc2.stop(now + 0.2);
  } catch (error) {
    // Graceful silence if Audio API is blocked or uninitiated
    console.debug("AI sound hover blocked or uninitiated:", error);
  }
}

/**
 * Synthesizes a celebratory high-tech clinical confirmation sweep tone.
 */
export function playAiSuccessSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.1);
    osc.frequency.exponentialRampToValueAtTime(1800, now + 0.25);

    gainNode.gain.setValueAtTime(0.08, now);
    gainNode.gain.exponentialRampToValueAtTime(0.04, now + 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

    osc.connect(gainNode);
    gainNode.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.4);
  } catch (error) {
    console.debug("AI success sound synthesis issue:", error);
  }
}
